import { useCallback, useEffect, useRef, useState } from "react";
import { atLeast, useBreakpoint, type Breakpoint } from "@/hooks/ui/useBreakpoint";
import { useResizeDrag, type ResizeDragHandle } from "@/hooks/useResizeDrag";

/**
 * The Testing workspace shell: how much room each panel gets, at every width.
 *
 * The module previously pinned three columns (240 sessions + 16 grip + 360 inspector = 616px of
 * chrome) with no way to collapse any of them, so below ~1280px the chat column was squeezed to
 * ~150px and the layout simply broke. This hook owns the arrangement instead:
 *
 *   full   — a resizable column
 *   rail   — a 44px icon strip (identity + counts stay visible, width does not)
 *   hidden — off-canvas; a reveal button brings it back
 *
 * Below `lg` a panel that is "full" renders as an OVERLAY drawer rather than an inline column, so
 * opening it never steals width from the chat. That is the mechanism that keeps the module usable at
 * 1024, 768 and below.
 *
 * The user always wins: a manual toggle sticks for as long as the viewport stays in that breakpoint.
 * Crossing a breakpoint re-applies the sensible default for the new size (a choice made at 1920 is
 * not a promise about what fits at 768).
 */

export type PanelMode = "full" | "rail" | "hidden";

/** Every panel dimension in one place; mirrored by the CSS custom properties in testing-tokens.css. */
export const TESTING_PANEL_LIMITS = {
	sessions: { min: 200, max: 360, default: 248 },
	inspector: { min: 280, max: 560, default: 340 },
	rail: 44,
} as const;

/** Default arrangement per breakpoint, widest to narrowest. */
const DEFAULT_MODES: Record<Breakpoint, { sessions: PanelMode; inspector: PanelMode }> = {
	"2xl": { sessions: "full", inspector: "full" },
	xl: { sessions: "full", inspector: "full" },
	// At 1024–1279 all three columns cannot coexist comfortably: the sessions list drops to a rail so
	// the chat keeps a readable measure while the live browser stays visible.
	lg: { sessions: "rail", inspector: "full" },
	// At 768–1023 the chat gets the whole width; both side panels are one tap away as overlays.
	md: { sessions: "hidden", inspector: "rail" },
	sm: { sessions: "hidden", inspector: "hidden" },
	xs: { sessions: "hidden", inspector: "hidden" },
};

export interface TestingPanel {
	mode: PanelMode;
	setMode: (mode: PanelMode) => void;
	/** full ⇄ (rail where it fits, hidden where it does not). */
	toggle: () => void;
	open: () => void;
	close: () => void;
	/** True when `mode === "full"` and the viewport is too narrow for an inline column. */
	isDrawer: boolean;
	/** True when the panel occupies real layout width right now. */
	isInline: boolean;
	width: number;
	drag: ResizeDragHandle;
}

export interface TestingShell {
	breakpoint: Breakpoint;
	/** Below `lg`: an opened panel floats over the workspace instead of taking width from it. */
	overlay: boolean;
	sessions: TestingPanel;
	inspector: TestingPanel;
	/** Dismiss whatever is floating — used by the scrim, Escape, and navigation. */
	closeDrawers: () => void;
}

export function useTestingShell(): TestingShell {
	const breakpoint = useBreakpoint();
	const overlay = !atLeast(breakpoint, "lg");

	const [sessionsMode, setSessionsMode] = useState<PanelMode>(DEFAULT_MODES[breakpoint].sessions);
	const [inspectorMode, setInspectorMode] = useState<PanelMode>(DEFAULT_MODES[breakpoint].inspector);

	// Re-apply defaults on a breakpoint CROSSING only — never on every render, and never while the
	// viewport stays put, so a manual toggle is not fought by this effect.
	const lastBreakpoint = useRef(breakpoint);
	useEffect(() => {
		if (lastBreakpoint.current === breakpoint) return;
		lastBreakpoint.current = breakpoint;
		setSessionsMode(DEFAULT_MODES[breakpoint].sessions);
		setInspectorMode(DEFAULT_MODES[breakpoint].inspector);
	}, [breakpoint]);

	const sessionsDrag = useResizeDrag({
		min: TESTING_PANEL_LIMITS.sessions.min,
		max: TESTING_PANEL_LIMITS.sessions.max,
		defaultValue: TESTING_PANEL_LIMITS.sessions.default,
	});
	const inspectorDrag = useResizeDrag({
		min: TESTING_PANEL_LIMITS.inspector.min,
		max: TESTING_PANEL_LIMITS.inspector.max,
		defaultValue: TESTING_PANEL_LIMITS.inspector.default,
		reverse: true,
	});

	/**
	 * Where a panel goes when it is collapsed: back to this breakpoint's own default, so closing is
	 * the exact inverse of opening. (Collapsing straight to "hidden" would silently remove the rail
	 * that the breakpoint had provided, moving the reveal control somewhere else after one toggle.)
	 */
	const collapsedModeFor = (side: "sessions" | "inspector"): PanelMode => {
		const preferred = DEFAULT_MODES[breakpoint][side];
		if (preferred !== "full") return preferred;
		return overlay ? "hidden" : "rail";
	};

	// The collapsed target depends on the breakpoint, which the callbacks below must not close over —
	// a ref keeps their identities stable while still reading the current value at call time.
	const collapsedRef = useRef({ sessions: "rail" as PanelMode, inspector: "rail" as PanelMode });
	collapsedRef.current = {
		sessions: collapsedModeFor("sessions"),
		inspector: collapsedModeFor("inspector"),
	};

	// Stable identities: these are handed to memoized panels, and a fresh closure per render defeated
	// their memo entirely (measured: the session list re-rendered on every streamed activity event).
	const toggleSessions = useCallback(
		() => setSessionsMode((m) => (m === "full" ? collapsedRef.current.sessions : "full")),
		[],
	);
	const openSessions = useCallback(() => setSessionsMode("full"), []);
	const closeSessions = useCallback(() => setSessionsMode(collapsedRef.current.sessions), []);
	const toggleInspector = useCallback(
		() => setInspectorMode((m) => (m === "full" ? collapsedRef.current.inspector : "full")),
		[],
	);
	const openInspector = useCallback(() => setInspectorMode("full"), []);
	const closeInspector = useCallback(() => setInspectorMode(collapsedRef.current.inspector), []);

	const makePanel = (
		mode: PanelMode,
		setMode: (m: PanelMode) => void,
		drag: ResizeDragHandle,
		actions: { toggle: () => void; open: () => void; close: () => void },
	): TestingPanel => ({
		mode,
		setMode,
		...actions,
		isDrawer: mode === "full" && overlay,
		isInline: mode !== "hidden" && !(mode === "full" && overlay),
		width: mode === "rail" ? TESTING_PANEL_LIMITS.rail : drag.width,
		drag,
	});

	const closeDrawers = useCallback(() => {
		if (!overlay) return;
		setSessionsMode((m) => (m === "full" ? "hidden" : m));
		setInspectorMode((m) => (m === "full" ? "hidden" : m));
	}, [overlay]);

	// Escape closes a floating panel — the same contract as every other overlay in the product.
	useEffect(() => {
		if (!overlay) return;
		if (sessionsMode !== "full" && inspectorMode !== "full") return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") closeDrawers();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [overlay, sessionsMode, inspectorMode, closeDrawers]);

	return {
		breakpoint,
		overlay,
		sessions: makePanel(sessionsMode, setSessionsMode, sessionsDrag, {
			toggle: toggleSessions,
			open: openSessions,
			close: closeSessions,
		}),
		inspector: makePanel(inspectorMode, setInspectorMode, inspectorDrag, {
			toggle: toggleInspector,
			open: openInspector,
			close: closeInspector,
		}),
		closeDrawers,
	};
}
